<template>
  <div class="hydraulic-jump-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$h₂/h₁ = 0.5 [√(1 + 8 Fr₁²) - 1]$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <rect x="-20" y="0" width="20" height="220" fill="#334155" />
          <path d="M 0,20 Q 20,40 20,60" fill="none" stroke="#64748b" stroke-width="4" />
          <rect x="0" y="200" width="400" height="20" fill="#475569" />
          
          <path :d="fluidSurfacePath" fill="rgba(6, 182, 212, 0.3)" stroke="#0ea5e9" stroke-width="3" />
          
          <g>
             <circle v-for="p in particles" :key="p.id" :cx="p.x" :cy="p.y" :r="p.r" fill="#e0f2fe" :opacity="p.opacity" />
          </g>

          <text x="30" y="190" fill="#f8fafc" font-size="10" font-weight="bold">超临界急流区 (Fr > 1)</text>
          <text x="250" y="190" fill="#f8fafc" font-size="10" font-weight="bold">亚临界缓流区 (Fr < 1)</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>上游弗劳德数 Fr₁</label>
              <span class="val">{{ Fr1.toFixed(2) }}</span>
           </div>
           <div class="hud-item orange">
              <label>跃后深度占比</label>
              <span class="val">{{ (h2 / h1).toFixed(2) }} <small>倍</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节上游流速 v₁ (增大引发更猛烈水跃)</label>
                    <el-slider v-model="velocity1" :min="3" :max="15" :step="0.5" size="small"></el-slider>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>水跃是明渠流体力学会从高速浅水的<strong>超临界流</strong>突变为低速深水的<strong>亚临界流</strong>。内置动量平衡 Belanger 方程自动推算下游水深 <code>h₂</code>。在跃水过渡区伴有极大紊流能量损失，粒子示踪实时展现连续性法则（浅而快 $\rightarrow$ 深而慢）。水库大坝正是利用在泄洪口修筑消力坎人为引发水跃来大量消耗凶猛动能，从而保护下游河床不遭冲刷。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HydraulicJumpViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      velocity1: 8,
      h1: 15,
      g: 9.8,
      jumpX: 130,
      jumpW: 60,
      particles: [],
      timer: null
    }
  },
  computed: {
    Fr1() { return this.velocity1 / Math.sqrt(this.g * this.h1 * 0.1); },
    h2() {
       let r = 0.5 * (Math.sqrt(1 + 8 * Math.pow(this.Fr1, 2)) - 1);
       return this.h1 * r;
    },
    fluidSurfacePath() {
       let y1 = 200 - this.h1;
       let y2 = 200 - this.h2;
       let x1 = this.jumpX;
       let x2 = this.jumpX + this.jumpW;
       return `M 0,200 L 0,${y1} L ${x1},${y1} 
               C ${x1 + this.jumpW*0.3},${y1} ${x1 + this.jumpW*0.5},${y2} ${x2},${y2} 
               L 400,${y2} L 400,200 Z`;
    }
  },
  methods: {
    initParticles() {
       for(let i=0; i<40; i++) this.spawnParticle();
    },
    spawnParticle(reset = false) {
       let p = {
          id: Math.random(),
          x: reset ? 0 : Math.random() * 400,
          y: 200 - Math.random() * this.h1,
          r: 1 + Math.random() * 2,
          opacity: 0.3 + Math.random() * 0.5,
          vx: 0, vy: 0, inTurbulence: false
       };
       if (!reset && p.x > this.jumpX + this.jumpW) p.y = 200 - Math.random() * this.h2;
       this.particles.push(p);
    },
    updateFlow() {
       let v2_actual = this.velocity1 * (this.h1 / this.h2); 
       for (let i = this.particles.length-1; i >= 0; i--) {
          let p = this.particles[i];
          if (p.x < this.jumpX) {
             p.vx = this.velocity1 * 2; p.vy = 0;
             if(p.y < 200 - this.h1) p.y = 200 - this.h1 + Math.random();
          } else if (p.x >= this.jumpX && p.x < this.jumpX + this.jumpW) {
             p.vx = (this.velocity1 + v2_actual);
             p.inTurbulence = true;
             p.vy = (Math.random() - 0.5) * 10;
             p.opacity = 0.8;
             let progress = (p.x - this.jumpX) / this.jumpW;
             let localH = this.h1 + (this.h2 - this.h1) * progress;
             if (p.y < 200 - localH) p.y = 200 - localH;
          } else {
             p.vx = v2_actual * 2; p.vy = 0; p.opacity = 0.3;
             if(p.inTurbulence) p.inTurbulence = false;
          }
          p.x += p.vx; p.y += p.vy;
          if (p.y > 195) p.y -= 2; 
          if (p.x > 400) { this.particles.splice(i, 1); this.spawnParticle(true); }
       }
       this.timer = requestAnimationFrame(this.updateFlow);
    }
  },
  mounted() { this.initParticles(); this.updateFlow(); },
  beforeDestroy() { if(this.timer) cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.hydraulic-jump-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
